import { IssueLabelsField } from '../../components/issues/IssueLabelsField';
import { IssueLabelChips } from '@overlayqa/shared/ui';
import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { SeverityBadge } from '../../components/shared/badges/SeverityBadge';
import { TypeBadge } from '../../components/shared/badges/TypeBadge';
import { StatusBadge } from '../../components/shared/badges/StatusBadge';
import { IssueThumbnail } from '../../components/shared/IssueThumbnail';
import type { ColumnDef, RowAction, BulkAction, SubMenuItem } from '../../components/shared/DataTableTypes';
import type { Ticket } from '@overlayqa/shared';
import { NotDetectedChip, markFixedWithUndo } from '../../components/issues/NotDetectedChip';
import { SelectDropdown } from '../../components/shared/SelectDropdown';
import { Avatar } from '../../components/ui/Avatar';
import { ISSUE_SEVERITY_OPTIONS, ISSUE_STATUS_OPTIONS, ISSUE_TYPE_PICKER_OPTIONS } from '../../lib/issueTypeOptions';
import { timeAgo } from '../../lib/utils';
import { getExportProviderLabel, issueTypeLabel, ISSUE_COPY_ITEMS, issueCopyLabel, type ClipboardFormat, type ExportProvider, type TicketSeverity, type TicketStatus } from '@overlayqa/shared';
import type { CopyIssuesMenu } from './copyIssues';

/** Re-export Ticket as Issue for column definitions */
export type Issue = Ticket;

/**
 * The one destination for an issue, called by BOTH the title link below and
 * IssuesPage's row click. A row that navigates somewhere other than its own
 * title link is worse than a row that does nothing, so the two are made
 * incapable of disagreeing rather than merely written to match.
 *
 * `displayId ?? id` is load-bearing: the detail route resolves either, but
 * export routes are UUID-only (see the 2026-07-21 fix), so the two identifier
 * shapes are not interchangeable everywhere and this one belongs to the link.
 */
export function issuePath(issue: Pick<Issue, 'id' | 'displayId'>): string {
  return `/issues/${issue.displayId ?? issue.id}`;
}

/** The read-only table, for every caller that does not edit in place. */
export const issueColumns: ColumnDef<Issue>[] = buildIssueColumns();

/** The three fields a row can change in place, and what each one's badge is. */
type InlineField = 'severity' | 'type' | 'status';
const BADGE_OF: Record<InlineField, (value: string) => ReactNode> = {
  severity: (value) => <SeverityBadge severity={value as TicketSeverity} />,
  type: (value) => <TypeBadge type={value} />,
  status: (value) => <StatusBadge status={value as TicketStatus} />,
};

export interface EditableIssueColumnOptions {
  /**
   * Called with the issue and the one field that changed. Passing it is what
   * makes the three cells editable; without it this builder returns the
   * read-only table every other caller already renders.
   */
  onFieldChange?: (issue: Issue, data: { severity?: TicketSeverity; type?: string; status?: TicketStatus }, via?: 'not_detected_chip') => void;
  teamMembers?: Array<{ id: string; name: string; avatarUrl?: string | null }>;
  membersLoading?: boolean;
  membersError?: boolean;
  onAssigneeChange?: (issue: Issue, assigneeId: string | null) => Promise<unknown>;
  /**
   * One more type to offer on this row, on top of the four a human may pick and
   * whatever the row currently carries. The parent passes the type the row
   * ARRIVED with, so a mis-click on a scanner-stamped row (A11y, Token, Debt)
   * can be undone: saving here is instant, and the shipped rule alone would
   * drop the stamped type from the list the moment the value moved off it.
   */
  alsoOfferType?: (issue: Issue) => string | undefined;
}

/**
 * The options for one cell: the pickable set, plus any value this row carries
 * or arrived with that the pickable set does not contain, each drawn as the
 * badge it will become. Never the same value twice.
 */
function optionsFor(field: InlineField, issue: Issue, alsoOfferType?: EditableIssueColumnOptions['alsoOfferType']) {
  const base = field === 'severity' ? ISSUE_SEVERITY_OPTIONS : field === 'status' ? ISSUE_STATUS_OPTIONS : ISSUE_TYPE_PICKER_OPTIONS;
  const extras = field === 'type'
    ? [issue.type, alsoOfferType?.(issue)].filter((value): value is string => Boolean(value) && !base.some((o) => o.value === value))
    : [];
  return [...base, ...[...new Set(extras)].map((value) => ({ value, label: issueTypeLabel(value) }))]
    .map((option) => ({ ...option, visual: BADGE_OF[field](option.value) }));
}

function editableCell(field: InlineField, label: string, { onFieldChange, alsoOfferType }: EditableIssueColumnOptions) {
  return (issue: Issue) => (
    <span className="inline-flex flex-wrap items-center gap-2">
      <SelectDropdown
        variant="badge"
        aria-label={label}
        value={issue[field] as string}
        options={optionsFor(field, issue, alsoOfferType)}
        onChange={(value) => onFieldChange?.(issue, { [field]: value })}
      />
      {field === 'status' && (
        <NotDetectedChip
          issue={issue}
          onMarkFixed={() => { void markFixedWithUndo(issue.status as TicketStatus, (status, via) => onFieldChange?.(issue, { status }, via)); }}
        />
      )}
    </span>
  );
}

function AssigneeCell({ issue, options }: { issue: Issue; options?: EditableIssueColumnOptions }) {
  const [saving, setSaving] = useState(false);
  const members = options?.teamMembers ?? [];
  const value = issue.assigneeId ?? '';
  const label = value ? members.find(member => member.id === value)?.name || 'Assigned teammate' : 'Unassigned';
  const memberOptions = [
    { value: '', label: 'Unassigned', leading: <span className="w-3.5 h-3.5 rounded-full border border-dashed border-gray-400 shrink-0" /> },
    ...members.map(member => ({ value: member.id, label: member.name, leading: <Avatar id={member.id} name={member.name} src={member.avatarUrl} size="xs" className="w-3.5 h-3.5 text-[9px]" /> })),
    ...(value && !members.some(member => member.id === value) ? [{ value, label, leading: <Avatar id={value} size="xs" className="w-3.5 h-3.5 text-[9px]" /> }] : []),
  ];
  if (!options?.onAssigneeChange) return <span className="flex items-center gap-2 max-w-44 text-xs text-gray-900" title={label}><span className="flex shrink-0" aria-hidden="true">{memberOptions.find(member => member.value === value)?.leading}</span><span className="truncate">{label}</span></span>;
  return (
    <SelectDropdown
      size="toolbar"
      aria-label={`Issue assignee: ${label}`}
      value={value}
      disabled={saving || options.membersLoading || options.membersError}
      options={memberOptions}
      onChange={async (next) => {
        if (next === value || saving) return;
        setSaving(true);
        try {
          await options.onAssigneeChange!(issue, next || null);
        } catch {
          // useInlineIssueUpdate owns rollback and the refusal toast.
        } finally {
          setSaving(false);
        }
      }}
    />
  );
}

/**
 * The issues table's columns. With no arguments this is the read-only table
 * (`issueColumns` above is exactly that call). Given `onFieldChange`, Severity /
 * Type / Status become one button each. Assignment has its own async callback
 * so the cell remains disabled until its save settles.
 *
 * The other three columns are deliberately never editable: Preview is a
 * picture, Created belongs to the system, and the title is the link that opens
 * the issue, so a control there would fight the click it already owns.
 */
export function buildIssueColumns(options?: EditableIssueColumnOptions): ColumnDef<Issue>[] {
  const cellFor = (field: InlineField, label: string) =>
    options?.onFieldChange ? editableCell(field, label, options) : (issue: Issue) => BADGE_OF[field](issue[field] as string);
  return [
    {
      // Leads the row (Emily's call, 2026-08-30): the eye lands on the capture,
      // then reads the title. `w-14` pins the column so a wide shot can never
      // squeeze the title column — DataTable applies className to BOTH the <th>
      // and the <td> (DataTable.tsx:240,292).
      header: 'Preview',
      className: 'w-14',
      render: (issue) => (
        <IssueThumbnail screenshotUrl={issue.screenshotUrl || issue.fullPageScreenshotUrl} type={issue.type} title={issue.title} />
      ),
    },
    {
      header: 'Issue',
      render: (issue) => (
        <Link to={issuePath(issue)} className="group block max-w-lg">
          <span className="oq-text-body text-gray-900 group-hover:text-primary-600 block transition-colors" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '32rem' }}>{issue.title}</span>
        </Link>
      ),
    },
    { header: 'Severity', render: cellFor('severity', 'Issue severity') },
    { header: 'Type', render: cellFor('type', 'Issue type') },
    { header: 'Status', render: cellFor('status', 'Issue status') },
    { header: 'Tags', render: issue => options?.onFieldChange ? <IssueLabelsField issueId={issue.id} labels={issue.labels} variant="inline" /> : <IssueLabelChips labels={issue.labels} /> },
    { header: 'Assignee', className: 'w-48', render: (issue) => <AssigneeCell issue={issue} options={options} /> },
    {
      header: 'Created',
      render: (issue) => {
        const created = new Date(issue.createdAt);
        return (
          <time
            className="text-xs text-gray-900"
            dateTime={created.toISOString()}
            title={created.toLocaleString()}
          >
            {timeAgo(issue.createdAt)}
          </time>
        );
      },
    },
  ];
}

interface AvailableProvider {
  provider: string;
  connected: boolean;
}

/**
 * Export submenu items for the row + bulk action menus. Every provider is
 * always listed as "Export to X"; unconnected providers are DISABLED with a
 * connect-hint tooltip instead of rendering a "Connect X" item that
 * navigates away (user decision 2026-07-04).
 */
export function buildExportSubmenu(
  availableProviders: AvailableProvider[],
  ids: string[],
  onExport: (ids: string[], provider: string) => void,
): SubMenuItem[] {
  return availableProviders.map(({ provider, connected }) => {
    const label = getExportProviderLabel(provider as ExportProvider) ?? provider;
    return {
      label: `Export to ${label}`,
      disabled: !connected,
      title: connected ? undefined : `Connect ${label} in Integrations to export issues.`,
      onClick: () => { onExport(ids, provider); },
    };
  });
}

/** One label, two entry points — the row kebab and the bulk toolbar. */
export const MOVE_TO_PROJECT_LABEL = 'Move to project…';

/**
 * Copy for saved issues (2026-10-25): the row kebab's and the bulk toolbar's
 * Copy category, the extension's and team review's (ccc143b29). Writes nothing,
 * so it needs no connected tracker and no write role. Sits after Export, before
 * Delete, the extension Actions order (Export, Copy, Share, ..., Delete).
 */
export const COPY_LABEL = 'Copy';

/** Submenu items from the shared labels: "Copy issue(s)", "Copy as Markdown", "Copy as CSV". */
function buildCopySubmenu(ids: string[], menu: CopyIssuesMenu, onCopy: (ids: string[], format: ClipboardFormat, menu: CopyIssuesMenu) => void): SubMenuItem[] {
  return ISSUE_COPY_ITEMS.map((format) => ({
    label: issueCopyLabel(format, ids.length),
    onClick: () => { onCopy(ids, format, menu); },
  }));
}

// Menu callbacks discard their return value. mutate handles rejected requests
// through the hook’s onError toast without leaving an unhandled promise.
export function buildIssueRowActions(opts: {
  bulkStatus: { mutate: (args: { ids: string[]; status: string }) => void };
  /** Ignore for issues (2026-09-22): the row menu routes one id through the bulk mutation. */
  bulkIgnore: { mutate: (args: { ids: string[]; ignored: boolean }) => void };
  availableProviders: AvailableProvider[];
  exportSubmenuForIds: (ids: string[]) => SubMenuItem[];
  setConfirmDelete: (v: { ids: string[] } | null) => void;
  /** Opens the destination picker. Moving is a two-step action: the issue's
   *  handle is re-minted in the destination, so it needs a confirmation the
   *  user can read before it happens. */
  onMoveClick: (ids: string[]) => void;
  /** Copies the issue to the clipboard in `format` (copyIssues.ts). */
  onCopy: (ids: string[], format: ClipboardFormat, menu: CopyIssuesMenu) => void;
}): RowAction<Issue>[] {
  const { bulkStatus, bulkIgnore, availableProviders, exportSubmenuForIds, setConfirmDelete, onMoveClick, onCopy } = opts;
  return [
    {
      label: 'Change status',
      onClick: () => {},
      getSubmenu: (issue) =>
        (['open', 'in-progress', 'resolved', 'verified', 'closed'] as const).map((s) => ({
          label: s.replace('-', ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
          checked: issue.status === s,
          onClick: () => { bulkStatus.mutate({ ids: [issue.id], status: s }); },
        })),
    },
    {
      // Not destructive: a move is reversible (move it back), unlike Delete.
      // Styling it red would put a recoverable action in the same visual class
      // as the one that is not.
      label: MOVE_TO_PROJECT_LABEL,
      onClick: (issue: Issue) => onMoveClick([issue.id]),
    },
    // Ignore / Un-ignore (2026-09-22): one or the other, never both. Reversible,
    // so it sits with Move above the destructive item; a flag, so the status
    // the issue had survives and Un-ignore restores it exactly.
    {
      label: 'Ignore',
      onClick: (issue: Issue) => { bulkIgnore.mutate({ ids: [issue.id], ignored: true }); },
      hidden: (issue: Issue) => !!issue.ignoredAt,
    },
    {
      label: 'Un-ignore',
      onClick: (issue: Issue) => { bulkIgnore.mutate({ ids: [issue.id], ignored: false }); },
      hidden: (issue: Issue) => !issue.ignoredAt,
    },
    ...(availableProviders.length > 0
      ? [{
          label: 'Export',
          onClick: () => {},
          getSubmenu: (issue: Issue) => exportSubmenuForIds([issue.id]),
        }]
      : []),
    {
      label: COPY_LABEL,
      onClick: () => {},
      getSubmenu: (issue: Issue) => buildCopySubmenu([issue.id], 'row', onCopy),
    },
    {
      label: 'Delete',
      onClick: (issue: Issue) => setConfirmDelete({ ids: [issue.id] }),
      destructive: true,
    },
  ];
}

/**
 * Returns the bulk-action toolbar definitions for the issues list.
 *
 * Verify fixes (Task 5.8):
 *  - Renders a "Verify fixes" button between Change Status and Export.
 *  - Disabled (with a tooltip) unless every selected issue's status is in
 *    (resolved, verified). The eligibility check needs the actual status of
 *    each selected row, so we accept an `issuesById` lookup map keyed by
 *    issue id.
 *  - On click, fires `onVerifyClick(selectedIds)`. The parent component owns
 *    the dialog + job state and the extension handoff (window.open with
 *    `#oq-verify-job=<jobId>` fragment), per spec §8.3 / plan decision #8.
 */
export function buildIssueBulkActions(opts: {
  bulkStatus: { mutate: (args: { ids: string[]; status: string }) => void };
  bulkIgnore: { mutateAsync: (args: { ids: string[]; ignored: boolean }) => Promise<unknown> };
  availableProviders: AvailableProvider[];
  exportSubmenuForIds: (ids: string[]) => SubMenuItem[];
  setConfirmDelete: (v: { ids: string[] } | null) => void;
  issuesById: Map<string, Issue>;
  onVerifyClick: (selectedIds: string[]) => void;
  /** Feature flag — when false, the "Verify fixes" action is omitted (prod gate). */
  verifyEnabled: boolean;
  /** Opens the destination picker for the current selection. */
  onMoveClick: (selectedIds: string[]) => void;
  /** Copies the selection to the clipboard in `format` (copyIssues.ts). */
  onCopy: (ids: string[], format: ClipboardFormat, menu: CopyIssuesMenu) => void;
}): BulkAction<Issue>[] {
  const { bulkStatus, bulkIgnore, availableProviders, exportSubmenuForIds, setConfirmDelete, issuesById, onVerifyClick, verifyEnabled, onMoveClick, onCopy } = opts;
  const verifyEligibleStatuses = new Set<Issue['status']>(['resolved', 'verified']);

  return [
    {
      label: 'Change status',
      onClick: () => {},
      getSubmenu: (ids) =>
        (['open', 'in-progress', 'resolved', 'verified', 'closed'] as const).map((s) => ({
          label: s.replace('-', ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
          onClick: () => { bulkStatus.mutate({ ids, status: s }); },
        })),
    },
    {
      label: MOVE_TO_PROJECT_LABEL,
      onClick: (ids: string[]) => onMoveClick(ids),
      isDisabled: (ids: string[]) => ids.length === 0,
    },
    // Ignore (2026-09-22): reversible, so it sits with Move, above Delete.
    {
      label: 'Ignore',
      // DataTable awaits this promise before clearing the selection.
      onClick: (ids: string[]) => bulkIgnore.mutateAsync({ ids, ignored: true }),
      isDisabled: (ids: string[]) => ids.length === 0,
    },
    ...(verifyEnabled
      ? [{
          label: 'Verify fixes',
          onClick: (ids: string[]) => onVerifyClick(ids),
          isDisabled: (ids: string[]) => {
            if (ids.length === 0) return true;
            return !ids.every((id) => {
              const issue = issuesById.get(id);
              return issue ? verifyEligibleStatuses.has(issue.status) : false;
            });
          },
          title: 'Only resolved/verified issues can be verified.',
        }]
      : []),
    ...(availableProviders.length > 0
      ? [{
          label: 'Export',
          onClick: () => {},
          getSubmenu: (ids: string[]) => exportSubmenuForIds(ids),
        }]
      : []),
    {
      label: COPY_LABEL,
      onClick: () => {},
      getSubmenu: (ids: string[]) => buildCopySubmenu(ids, 'bulk', onCopy),
      isDisabled: (ids: string[]) => ids.length === 0,
      // The copied issues are still the selection; the extension and team review keep it too.
      clearSelectionOnSuccess: false,
    },
    {
      label: 'Delete',
      onClick: (ids: string[]) => setConfirmDelete({ ids }),
      clearSelectionOnSuccess: false,
      destructive: true,
    },
  ];
}
